package one.healthium.onboarding.ui.screens

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import one.healthium.bundle.components.BundleImage
import one.healthium.domain.user.models.prognoseWeightArray
import one.healthium.onboarding.domain.configs.PersonalPlanScreenConfig
import one.healthium.onboarding.ui.UserDataProvider
import one.healthium.onboarding.ui.UserProfileUiModel
import one.healthium.onboarding.ui.bindPlaceholderValues
import one.healthium.onboarding.ui.components.AnimatableAppText
import one.healthium.onboarding.ui.components.PrognoseWeightChart
import one.healthium.ui.common.utils.toColor
import one.healthium.ui.common.utils.toTextStyle
import one.healthium.ui.components.CtaButton
import one.healthium.ui.compose.components.AppScaffold
import one.healthium.ui.compose.components.AppText
import one.healthium.ui.compose.theme.AppTheme

interface PersonalPlanEventDelegate {
    fun onNextClicked()
}

@Composable
internal fun PersonalPlanScreen(
    eventDelegate: PersonalPlanEventDelegate,
    screenConfig: PersonalPlanScreenConfig,
) {
    UserDataProvider { userState, _ ->
        AppScaffold {
            PersonalPlanScreenContent(
                screenConfig = screenConfig,
                userState = userState,
                onNextClicked = eventDelegate::onNextClicked,
            )
        }
    }
}

@Composable
internal fun PersonalPlanScreenContent(
    modifier: Modifier = Modifier,
    screenConfig: PersonalPlanScreenConfig,
    userState: UserProfileUiModel,
    onNextClicked: () -> Unit,
) {
    Column(
        modifier = modifier
            .fillMaxSize()
            .navigationBarsPadding()
            .verticalScroll(rememberScrollState()),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Header(screenConfig, userState)
        Spacer(Modifier.height(24.dp))
        PrognoseWeightChart(
            modifier = Modifier
                .fillMaxWidth()
                .height(195.dp)
                .padding(horizontal = 24.dp),
            prognoseWeight = userState.model.prognoseWeightArray(screenConfig.period),
            isMetric = userState.isMetric,
        )
        Spacer(Modifier.height(36.dp))
        AppText(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 41.dp),
            text = screenConfig.bottomSubtitle?.bindPlaceholderValues(userState),
            style = AppTheme.textStyles.caption,
            color = AppTheme.colors.text100,
            textAlign = TextAlign.Center,
        )
        Spacer(Modifier.weight(1f))
        Spacer(Modifier.height(16.dp))
        CtaButton(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 38.dp),
            onClick = onNextClicked,
            text = screenConfig.ctaTitle,
        )
        Spacer(Modifier.height(24.dp))
    }
}

@Composable
private fun Header(
    screenConfig: PersonalPlanScreenConfig,
    userState: UserProfileUiModel
) {
    Box {
        BundleImage(
            modifier = Modifier
                .fillMaxWidth()
                .height(246.dp),
            name = screenConfig.image,
            contentScale = ContentScale.FillBounds,
        )
        AnimatableAppText(
            modifier = Modifier
                .fillMaxWidth()
                .statusBarsPadding()
                .align(Alignment.Center)
                .padding(top = 10.dp)
                .padding(horizontal = 24.dp),
            text = screenConfig.title,
            style = screenConfig.titleStyle.toTextStyle(AppTheme.textStyles.headerRegular),
            color = screenConfig.titleStyle?.color?.toColor() ?: AppTheme.colors.textInverted100,
            textAlign = TextAlign.Center,
            userState = userState,
        )
    }
}
